import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, User, Phone, CheckCircle2, Volume2, VolumeX, Loader2, Square, Mic } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem("ltr_chat_session");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("ltr_chat_session", id);
  }
  return id;
}

const SUGGESTIONS = [
  "Help me find the right loan program",
  "I want to fix & flip a property",
  "I need working capital for my business",
  "How fast can I get funded?",
];

const GREETING_TEXT =
  "Hi, welcome to LTR Capital Partners. I'm your Capital Assistant. Tell me about your project, and I'll match you with the best loan program.";

let _msgSeq = 0;
const nextMsgId = () => `m${Date.now().toString(36)}_${++_msgSeq}`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const leadKey = "ltr_chat_lead_" + sessionId.current;
  const [leadDone, setLeadDone] = useState(!!localStorage.getItem(leadKey));
  const [leadDismissed, setLeadDismissed] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "" });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const scrollRef = useRef(null);
  const endRef = useRef(null);

  // ---- Voice (ElevenLabs TTS) ----
  const audioRef = useRef(null);
  const audioCache = useRef({});
  const [voiceMsgId, setVoiceMsgId] = useState(null);   // message currently playing
  const [voiceLoadingId, setVoiceLoadingId] = useState(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("ltr_voice_on") === "1");

  const stopVoice = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setVoiceMsgId(null);
  }, []);

  const playVoice = useCallback(async (msg) => {
    // Toggle off if this message is already playing
    if (voiceMsgId === msg.id) {
      stopVoice();
      return;
    }
    stopVoice();
    const text = (msg.content || "").trim();
    if (!text) return;
    setVoiceLoadingId(msg.id);
    try {
      let src = audioCache.current[msg.id];
      if (!src) {
        const res = await fetch(`${API}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!res.ok) throw new Error("tts failed");
        const data = await res.json();
        src = data.audio;
        audioCache.current[msg.id] = src;
      }
      const audio = new Audio(src);
      audioRef.current = audio;
      audio.onended = () => setVoiceMsgId((cur) => (cur === msg.id ? null : cur));
      audio.onerror = () => setVoiceMsgId((cur) => (cur === msg.id ? null : cur));
      setVoiceMsgId(msg.id);
      await audio.play();
    } catch {
      setVoiceMsgId(null);
    } finally {
      setVoiceLoadingId((cur) => (cur === msg.id ? null : cur));
    }
  }, [voiceMsgId, stopVoice]);

  // Stop any audio when the panel closes
  useEffect(() => { if (!open) stopVoice(); }, [open, stopVoice]);

  const toggleVoiceOn = useCallback(() => {
    setVoiceOn((v) => {
      const next = !v;
      localStorage.setItem("ltr_voice_on", next ? "1" : "0");
      if (!next) stopVoice();
      return next;
    });
  }, [stopVoice]);

  // Speak a short welcome the moment the chat opens (only when voice is enabled)
  const greetedRef = useRef(false);
  useEffect(() => {
    if (open && voiceOn && !greetedRef.current && messages.length === 0) {
      greetedRef.current = true;
      playVoice({ id: "greeting", content: GREETING_TEXT });
    }
  }, [open, voiceOn, messages.length, playVoice]);

  // ---- Voice input (ElevenLabs STT via mic) ----
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const toggleRecord = useCallback(async () => {
    if (recording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (!blob.size) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "speech.webm");
          const res = await fetch(`${API}/stt`, { method: "POST", body: fd });
          if (!res.ok) throw new Error("stt failed");
          const data = await res.json();
          if (data.text) setInput((prev) => (prev ? prev + " " : "") + data.text);
        } catch { /* ignore */ } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch { /* mic permission denied */ }
  }, [recording]);

  // Show the lead form once the assistant has given a recommendation
  const recommended = messages.some(
    (m) => m.role === "assistant" && /best fit/i.test(m.content)
  );
  const showLead = recommended && !leadDone && !leadDismissed && !loading;

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !lead.phone.trim() || leadSubmitting) return;
    setLeadSubmitting(true);
    try {
      const res = await fetch(`${API}/chat/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId.current,
          name: lead.name.trim(),
          phone: lead.phone.trim(),
          email: lead.email.trim(),
        }),
      });
      if (!res.ok) throw new Error("Failed");
      localStorage.setItem(leadKey, "1");
      setLeadDone(true);
      setMessages((m) => [
        ...m,
        {
          id: nextMsgId(),
          role: "assistant",
          content: `Thanks, ${lead.name.trim()}! A lending specialist will reach out shortly with exact terms. In the meantime you can start your free application anytime at /apply.`,
        },
      ]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          id: nextMsgId(),
          role: "assistant",
          content: "Sorry, I couldn't save your details just now. Please call us at (844) LENDER1 and we'll help right away.",
        },
      ]);
    } finally {
      setLeadSubmitting(false);
    }
  };

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [messages, open, showLead, scrollToBottom]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { id: nextMsgId(), role: "user", content }]);
    setLoading(true);

    // Placeholder for streaming assistant message
    const assistantId = nextMsgId();
    setMessages((m) => [...m, { id: assistantId, role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: content }),
      });
      if (!res.ok || !res.body) throw new Error("Network error");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: acc };
          return copy;
        });
      }
      if (voiceOn && acc.trim()) {
        playVoice({ id: assistantId, content: acc });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          role: "assistant",
          content: "Sorry, I'm having trouble right now. Please call us at (844) LENDER1.",
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Allow other pages (e.g. loan program pages) to open the chat with a preset question
  const sendRef = useRef(send);
  useEffect(() => { sendRef.current = send; });
  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const msg = e.detail && e.detail.message;
      if (msg) setTimeout(() => sendRef.current && sendRef.current(msg), 450);
    };
    window.addEventListener("ltr-open-chat", handler);
    return () => window.removeEventListener("ltr-open-chat", handler);
  }, []);

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat assistant"
        className={`fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          open ? "bg-[#0b1f3a] rotate-90" : "bg-[#c6a15b] hover:scale-105"
        }`}
      >
        {open ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 text-[#0b1f3a]" />
        )}
      </button>

      {/* Panel */}
      <div
        className={`fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] origin-bottom-right transition-all duration-300 ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden flex flex-col h-[540px] max-h-[75vh]">
          {/* Header */}
          <div className="bg-[#0b1f3a] px-5 py-4 relative">
            <button
              type="button"
              data-testid="chat-voice-toggle"
              onClick={toggleVoiceOn}
              aria-label={voiceOn ? "Turn voice replies off" : "Turn voice replies on"}
              title={voiceOn ? "Voice replies on" : "Voice replies off"}
              className={`absolute top-4 right-4 h-8 w-8 rounded-lg flex items-center justify-center transition ${
                voiceOn ? "bg-[#c6a15b] text-[#0b1f3a]" : "bg-white/10 text-slate-300 hover:bg-white/20"
              }`}
            >
              {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
            <img src="/logo-white.png" alt="LTR Capital Partners" className="h-6 w-auto object-contain" />
            <div className="mt-2 text-white">
              <div className="font-display font-bold leading-tight text-sm">Capital Assistant</div>
              <div className="text-xs text-slate-300 flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-green-400 inline-block" /> Online · smart loan matching
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-[#c6a15b]/15 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-[#b08e46]" />
                </div>
                <p className="mt-4 text-slate-700 font-medium">Hi! I'm the LTR Capital assistant.</p>
                <p className="mt-1 text-sm text-slate-500">Tell me about your project and I'll match you with the best loan program.</p>
                <div className="mt-5 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="text-left text-sm rounded-lg bg-white ring-1 ring-slate-200 px-3 py-2.5 text-slate-700 hover:ring-[#c6a15b] hover:text-[#0b1f3a] transition"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={m.id} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0b1f3a] text-white rounded-br-md"
                      : "bg-white text-slate-800 ring-1 ring-slate-100 rounded-bl-md"
                  }`}
                >
                  {m.content || (loading && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1">
                      <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="h-2 w-2 rounded-full bg-slate-300 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </span>
                  ) : "")}
                </div>
                {m.role === "assistant" && m.content && !(loading && i === messages.length - 1) && (
                  <button
                    type="button"
                    data-testid={`chat-tts-btn-${m.id}`}
                    onClick={() => playVoice(m)}
                    className="mt-1 ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-[#b08e46] transition"
                    aria-label={voiceMsgId === m.id ? "Stop audio" : "Listen to this reply"}
                  >
                    {voiceLoadingId === m.id ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</>
                    ) : voiceMsgId === m.id ? (
                      <><Square className="h-3.5 w-3.5 fill-current" /> Stop</>
                    ) : (
                      <><Volume2 className="h-3.5 w-3.5" /> Listen</>
                    )}
                  </button>
                )}
              </div>
            ))}
            {showLead && (
              <div data-testid="chat-lead-card" className="rounded-2xl bg-white ring-1 ring-[#c6a15b]/40 shadow-sm p-4">
                <div className="flex items-center gap-2">
                  <span className="h-8 w-8 rounded-lg bg-[#c6a15b]/15 flex items-center justify-center">
                    <Phone className="h-4 w-4 text-[#b08e46]" />
                  </span>
                  <div>
                    <div className="text-sm font-bold text-[#0b1f3a] leading-tight">Get a free consultation</div>
                    <div className="text-[11px] text-slate-500">Leave your details — a specialist will call with exact terms.</div>
                  </div>
                </div>
                <form onSubmit={submitLead} className="mt-3 space-y-2">
                  <div className="relative">
                    <User className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="chat-lead-name"
                      value={lead.name}
                      onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                      placeholder="Full name"
                      className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition"
                    />
                  </div>
                  <div className="relative">
                    <Phone className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="chat-lead-phone"
                      value={lead.phone}
                      onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))}
                      placeholder="Phone number"
                      inputMode="tel"
                      className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition"
                    />
                  </div>
                  <input
                    data-testid="chat-lead-email"
                    value={lead.email}
                    onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                    placeholder="Email (optional)"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition"
                  />
                  <div className="flex items-center gap-2 pt-0.5">
                    <button
                      type="submit"
                      data-testid="chat-lead-submit"
                      disabled={leadSubmitting || !lead.name.trim() || !lead.phone.trim()}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#c6a15b] px-4 py-2.5 text-sm font-bold text-[#0b1f3a] hover:bg-[#d8bc85] transition disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      {leadSubmitting ? "Sending..." : "Request a call"}
                    </button>
                    <button
                      type="button"
                      data-testid="chat-lead-dismiss"
                      onClick={() => setLeadDismissed(true)}
                      className="text-xs font-semibold text-slate-400 hover:text-slate-600 px-2"
                    >
                      Maybe later
                    </button>
                  </div>
                </form>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-100 p-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder={transcribing ? "Transcribing your voice…" : "Type your question..."}
                className="flex-1 resize-none max-h-24 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-[#c6a15b] focus:ring-2 focus:ring-[#c6a15b]/20 outline-none transition"
              />
              <button
                onClick={toggleRecord}
                disabled={transcribing || loading}
                data-testid="chat-mic-btn"
                aria-label={recording ? "Stop recording" : "Speak your question"}
                title={recording ? "Stop recording" : "Speak your question"}
                className={`h-10 w-10 rounded-xl flex items-center justify-center transition disabled:opacity-50 shrink-0 ${
                  recording
                    ? "bg-red-500 text-white animate-pulse"
                    : "bg-slate-100 text-[#0b1f3a] hover:bg-slate-200"
                }`}
              >
                {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
              </button>
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                className="h-10 w-10 rounded-xl bg-[#c6a15b] flex items-center justify-center text-[#0b1f3a] hover:bg-[#d8bc85] transition disabled:opacity-50 shrink-0"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-[10px] text-slate-400 text-center">AI assistant · not a loan offer</p>
          </div>
        </div>
      </div>
    </>
  );
}
